import first from 'lodash/fp/first';
import getOr from 'lodash/fp/getOr';
import * as Sentry from '@sentry/browser';
import { push } from 'redux-first-history';

// Relies on all other checkout sub-domain actions
import { CLEAR_CHECKOUT } from 'dux/checkout/actionTypes';

import { getNextStepFromStepProp } from 'dux/checkout/selectors';

import { loadAddresses, saveContactAddress } from 'dux/checkoutAddresses/actions';
import { getAddressesStatus } from 'dux/checkoutAddresses/selectors';

import { loadCart } from 'dux/checkoutCart/actions';
import { getCartData, getCartStatus } from 'dux/checkoutCart/selectors';

import { orderLoadSuccessFromPaymentSuccess } from 'dux/checkoutOrder/actions';

import { savePayment } from 'dux/checkoutPayment/actions';
import { getStripeToken } from 'dux/checkoutPayment/selectors';

import { trackPurchaseCompleted } from 'dux/tracking/actions';

import { isPaymentInvalid } from 'Services/HTTPError';
import * as OrderService from 'Services/OrderService';

import addOriginParam from 'utils/addOriginParam';

/**
 * Internal store
 */

export const clearCheckoutState = () => ({
  type: CLEAR_CHECKOUT,
});

/**
 * FETCH (addresses) and POST (cart)
 */

export const loadCheckout = params => async (dispatch, getState) => {
  const state = getState();

  await Promise.all([
    getAddressesStatus(state) === 'idle' && dispatch(loadAddresses()),
    getCartStatus(state) === 'idle' && dispatch(loadCart(params)),
  ]);
};

/**
 * POST
 */

const finishCheckout = ({ values }) => async (dispatch, getState) => {
  const state = getState();
  const cart = getCartData(state);
  const cardPubkey = values.cardPubkey === 'newCard' ? null : values.cardPubkey;
  const saveCard = getOr(false, 'saveCard')(values);
  const token = getStripeToken(state);

  try {
    if (cart.need_address && !cart.shipping_address) {
      return {
        status: 'failure',
        addressError: 'Cannot place order without a shipping address.',
      };
    }

    // This change should be backward compatible, here we introduce logic only if new field is set
    if (cardPubkey) {
      const order =
        cart.total_price_with_taxes === 0
          ? await OrderService.postFreeOrder()
          : await OrderService.postWithSavedPayment(cardPubkey);

      dispatch(orderLoadSuccessFromPaymentSuccess(order));

      return { status: 'success', order };
    }

    const order =
      cart.total_price_with_taxes === 0
        ? await OrderService.postFreeOrder()
        : await OrderService.post(token.id, saveCard);

    dispatch(orderLoadSuccessFromPaymentSuccess(order));

    return { status: 'success', order };
  } catch (error) {
    if (isPaymentInvalid(error)) {
      return { status: 'failure', cardError: error.detail };
    }
    return { status: 'failure' };
  }
};

/**
 * PATCH (contact) and POST (payment + finish Checkout)
 */

export const saveCheckoutStep = params => dispatch => {
  switch (params.step) {
    case 'contact-address':
      return dispatch(saveContactAddress(params));
    case 'payment':
      return dispatch(savePayment(params));
    case 'confirmation':
      return dispatch(finishCheckout(params));
    default:
      return null;
  }
};

// Newer actions used for the new checkout flow, logic is 1-1 copy of legacy
export const stepByStepCheckoutSubmit =
  // From react props

  ({ step, stripe, params, cardElement }) =>
    // From formik callback
    (values, actions) =>
      // From redux thunk
      async (dispatch, getState) => {
        const { status, order, cardError, addressError } = await dispatch(
          saveCheckoutStep({
            values,
            step,
            stripe,
            cardElement,
          })
        );
        const nextStep = getNextStepFromStepProp(getState(), { step });
        const withOrigin = addOriginParam(params);

        if (status === 'success') {
          if (nextStep === 'success') {
            dispatch(
              trackPurchaseCompleted({
                express_checkout: false,
                payment_location: 'cart',
                payment_type: 'Credit Card',
              })
            );
            dispatch(push(`/checkout/success?order-pubkey=${order.pubkey}`));
          } else {
            dispatch(push(withOrigin(`/checkout/${nextStep}`)));
          }
        }

        if (cardError) {
          actions.setFieldValue('cardError', cardError);
          dispatch(push('/checkout/payment'));
        }

        if (addressError) {
          if (addressError?.extra?.zipcode?.length) {
            actions.setFieldError('newAddress.zipcode', first(addressError?.extra?.zipcode));
          }
          Sentry.captureMessage(addressError);
          dispatch(push('/checkout/contact-address'));
        }

        actions.setSubmitting(false);
      };

export const expressCheckoutSubmit = ({
  payerPhone,
  paymentType,
  shippingAddress,
  shippingOption,
  token,
}) => async (dispatch, getState) => {
  const state = getState();
  const cart = getCartData(state);

  try {
    const { addressError } = await dispatch(
      saveContactAddress({
        values: {
          addressPubkey: 'newAddress',
          phone: (payerPhone || shippingAddress?.phone)?.replace(/\s/g, ''),
          newAddress: {
            address1: shippingAddress?.addressLine?.[0],
            city: shippingAddress?.city,
            label: shippingOption?.label,
            name: shippingAddress?.recipient,
            state: shippingAddress?.region,
            zipcode: shippingAddress?.postalCode,
          },
        },
      })
    );

    if (addressError) {
      Sentry.captureMessage(addressError);
      return { status: 'failure' };
    }

    console.warn('cart total_price_with_taxes :', cart?.total_price_with_taxes);

    const order =
      cart.total_price_with_taxes === 0
        ? await OrderService.postFreeOrder()
        : await OrderService.post(token?.id, false);

    dispatch(orderLoadSuccessFromPaymentSuccess(order));
    dispatch(
      trackPurchaseCompleted({
        express_checkout: true,
        payment_location: 'cart',
        payment_type: paymentType,
      })
    );

    return { status: 'success', order };
  } catch (error) {
    return isPaymentInvalid(error)
      ? { status: 'failure', cardError: error.detail }
      : { status: 'failure' };
  }
};
